import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Vérifier le token au chargement de l'app
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      setLoading(true);
      console.log('🔍 Vérification du statut d\'authentification...');
      
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.log('❌ Aucun token trouvé');
        setUser(null);
        setLoading(false);
        return;
      }

      console.log('✅ Token trouvé, vérification de la validité...');
      
      // Vérifier la validité du token
      const isValid = await authService.verifyToken();
      
      if (isValid) {
        console.log('✅ Token valide, récupération du profil...');
        // Récupérer les infos utilisateur
        const profileResponse = await authService.getProfile();
        
        if (profileResponse.success) {
          // ✅ CORRECTION : Le backend renvoie déjà les bons noms de champs (first_name, last_name)
          console.log('📥 Données profil reçues:', profileResponse.data);
          setUser(profileResponse.data);
          
          // ✅ Mettre à jour le cache localStorage avec les données du profil
          localStorage.setItem('userInfo', JSON.stringify(profileResponse.data));
          
          console.log('✅ Utilisateur connecté:', profileResponse.data);
        } else {
          console.error('❌ Échec récupération profil:', profileResponse.message);
          // Token valide mais profil inaccessible, nettoyer
          localStorage.removeItem('token');
          localStorage.removeItem('userInfo');
          setUser(null);
          setError(profileResponse.message);
        }
      } else {
        console.warn('❌ Token invalide');
        // Token invalide, nettoyer
        localStorage.removeItem('token');
        localStorage.removeItem('userInfo');
        setUser(null);
      }
    } catch (error) {
      console.error('❌ Erreur lors de la vérification du statut:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('userInfo');
      setUser(null);
      setError('Erreur de vérification de session');
    } finally {
      setLoading(false);
    }
  };

  const login = async (credentials) => {
    try {
      setError(null);
      setLoading(true);
      console.log('🔐 Démarrage du processus de connexion...');
      
      const response = await authService.login(credentials);
      console.log('📥 Réponse de connexion:', response);
      
      if (response.success) {
        console.log('✅ Connexion réussie');
        
        // ✅ CORRECTION: Toujours récupérer le profil complet après une connexion réussie
        // Cela garantit que nous avons toutes les informations utilisateur (first_name, last_name, etc.)
        console.log('🔄 Récupération du profil complet après connexion...');
        const profileResponse = await authService.getProfile();
        
        if (profileResponse.success) {
          console.log('📥 Profil complet récupéré:', profileResponse.data);
          setUser(profileResponse.data);
          
          // ✅ Mettre à jour le cache avec le profil complet
          localStorage.setItem('userInfo', JSON.stringify(profileResponse.data));
          
          console.log('✅ Utilisateur mis à jour avec profil complet:', profileResponse.data);
          return { success: true };
        } else {
          console.warn('⚠️ Échec récupération profil après connexion');
          
          // ✅ Fallback : utiliser les données de base de la réponse de login si disponibles
          if (response.data?.user) {
            setUser(response.data.user);
            localStorage.setItem('userInfo', JSON.stringify(response.data.user));
            console.log('✅ Utilisateur défini depuis données de login:', response.data.user);
            return { success: true };
          }
          
          // ✅ Dernier fallback : créer un utilisateur minimal depuis le token
          const token = localStorage.getItem('token');
          if (token) {
            try {
              const tokenPayload = JSON.parse(atob(token.split('.')[1]));
              const minimalUser = {
                id: tokenPayload.sub,
                email: tokenPayload.email || credentials?.username || 'utilisateur@inconnu.com',
                role: tokenPayload.role,
                first_name: tokenPayload.first_name || 'Utilisateur',
                last_name: tokenPayload.last_name || '',
                username: tokenPayload.preferred_username || credentials?.username || 'utilisateur'
              };
              setUser(minimalUser);
              localStorage.setItem('userInfo', JSON.stringify(minimalUser));
              console.log('✅ Utilisateur créé depuis token:', minimalUser);
              return { success: true };
            } catch (tokenError) {
              console.error('❌ Erreur décodage token:', tokenError);
            }
          }
          
          setError('Erreur lors de la récupération du profil');
          return { success: false, error: 'Erreur lors de la récupération du profil' };
        }
      } else {
        console.error('❌ Échec de connexion:', response.message);
        setError(response.message);
        return { success: false, error: response.message };
      }
    } catch (error) {
      console.error('❌ Erreur lors de la connexion:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Erreur de connexion';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    console.log('🚪 Déconnexion...');
    localStorage.removeItem('token');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('selectedBoutique');
    setUser(null);
    setError(null);
    console.log('✅ Déconnexion terminée');
  };

  const updateUserProfile = async (updatedData) => {
    try {
      setLoading(true);
      console.log('🔄 Mise à jour du profil utilisateur...');
      
      const response = await authService.updateProfile(updatedData);
      
      if (response.success) {
        console.log('📥 Profil mis à jour:', response.data);
        setUser(response.data);
        
        // ✅ Mettre à jour le cache également
        localStorage.setItem('userInfo', JSON.stringify(response.data));
        
        console.log('✅ Profil utilisateur mis à jour avec succès');
        return { success: true };
      } else {
        setError(response.message);
        return { success: false, error: response.message };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Erreur de mise à jour';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  /**
   * ✅ Fonction pour rafraîchir les données utilisateur
   */
  const refreshUser = async () => {
    try {
      console.log('🔄 Rafraîchissement des données utilisateur...');
      
      const profileResponse = await authService.getProfile();
      
      if (profileResponse.success) {
        console.log('📥 Données rafraîchies:', profileResponse.data);
        setUser(profileResponse.data);
        localStorage.setItem('userInfo', JSON.stringify(profileResponse.data));
        console.log('✅ Données utilisateur rafraîchies avec succès');
        return { success: true, data: profileResponse.data };
      } else {
        console.error('❌ Échec rafraîchissement profil:', profileResponse.message);
        setError(profileResponse.message);
        return { success: false, error: profileResponse.message };
      }
    } catch (error) {
      console.error('❌ Erreur lors du rafraîchissement:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Erreur de rafraîchissement';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const hasRole = (roles) => {
    if (!user) return false;
    if (Array.isArray(roles)) {
      return roles.includes(user.role);
    }
    return user.role === roles;
  };

  const canAccessBoutique = (boutiqueId) => {
    if (!user) return false;
    
    // Super admin peut accéder à toutes les boutiques
    if (user.role === 'SUPER_ADMIN') return true;
    
    // Manager et Employee ne peuvent accéder qu'à leur boutique
    return user.boutique?.id === boutiqueId || user.boutiqueId === boutiqueId || user.boutique_id === boutiqueId;
  };

  const value = {
    // État
    user,
    loading,
    error,
    
    // Actions
    login,
    logout,
    updateUserProfile,
    refreshUser,
    checkAuthStatus,
    clearError: () => setError(null),
    
    // Helpers
    hasRole,
    canAccessBoutique,
    isAuthenticated: !!user && !!localStorage.getItem('token'),
    isSuperAdmin: user?.role === 'SUPER_ADMIN',
    isManager: user?.role === 'MANAGER',
    isEmployee: user?.role === 'EMPLOYEE'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;